import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";

export default function Nav() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeLink, setActiveLink] = useState("");
  const [programsOpen, setProgramsOpen] = useState(false);

  const closeMenu = useCallback(() => {
    setIsOpen(false);
    document.body.style.overflow = "";
  }, []);

  const openMenu = useCallback(() => {
    setIsOpen(true);
    document.body.style.overflow = "hidden";
  }, []);

  const toggleMenu = () => (isOpen ? closeMenu() : openMenu());

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 20);
      const scrollY = window.scrollY + 100;
      const ids = ["programs", "store", "testimonials", "faq"];
      let current = "";
      ids.forEach((id) => {
        const el = document.getElementById(id);
        if (el && el.offsetTop <= scrollY) current = id;
      });
      setActiveLink(current);
    };

    const onKey = (e) => {
      if (e.key === "Escape") {
        if (isOpen) closeMenu();
        setProgramsOpen(false);
      }
    };

    // close dropdown on outside click
    const onOutsideClick = (e) => {
      if (!e.target.closest(".programs-dropdown-wrap")) {
        setProgramsOpen(false);
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    document.addEventListener("keydown", onKey);
    document.addEventListener("click", onOutsideClick);
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("click", onOutsideClick);
    };
  }, [isOpen, closeMenu]);

  // close dropdown on scroll
  useEffect(() => {
    if (programsOpen) setProgramsOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrolled]);

  const otherLinks = [
    { href: "#store", label: "المتجر" },
    { href: "#testimonials", label: "تجارب المتدربين" },
    { href: "#faq", label: "الأسئلة الشائعة" },
  ];

  const mobileLinks = [
    { href: "#store", label: "المتجر" },
    { href: "#proof", label: "مشاريع الخريجين" },
    { href: "#testimonials", label: "تجارب المتدربين" },
    { href: "#faq", label: "الأسئلة الشائعة" },
  ];

  return (
    <>
      <nav
        aria-label="التنقل الرئيسي"
        className={[
          "fixed top-0 left-0 right-0 z-[500] flex items-center justify-between h-[var(--nav-h)]",
          "px-[clamp(16px,5vw,60px)]",
          "border-b border-[rgba(109,211,167,0.13)]",
          "font-ibm transition-all duration-300",
          scrolled
            ? "bg-[rgba(4,14,24,0.97)] shadow-[0_2px_24px_rgba(0,0,0,.4)]"
            : "bg-[rgba(8,24,40,0.92)] backdrop-blur-xl",
          isOpen ? "menu-open" : "",
        ].join(" ")}
      >
        {/* Logo */}
        <a
          href="/"
          className="flex items-center gap-2 no-underline font-cairo flex-shrink-0"
          aria-label="آفاق - الصفحة الرئيسية"
        >
          <span className="text-mint text-base leading-none" aria-hidden="true">
            ◆
          </span>
          <span className="text-[1.2rem] font-bold text-t-primary tracking-tight">
            آفاق
          </span>
          <span className="w-px h-[15px] bg-t-dim mx-0.5" aria-hidden="true" />
          <span className="text-[.58rem] text-t-sec font-normal font-ibm">
            وتان
          </span>
        </a>

        {/* ── Desktop links ── */}
        <ul
          className="hidden md:flex items-center gap-[clamp(1.2rem,2.5vw,2.2rem)] list-none"
          role="list"
        >
          {/* البرامج — with dropdown */}
          <li className="programs-dropdown-wrap relative">
            <button
              onClick={() => setProgramsOpen((v) => !v)}
              className={`nav-link flex items-center gap-1 bg-transparent border-0 cursor-pointer p-0 ${
                activeLink === "programs" ? "active" : ""
              }`}
              aria-expanded={programsOpen}
              aria-haspopup="true"
            >
              البرامج
              <svg
                className={`w-[10px] h-[10px] transition-transform duration-200 ${programsOpen ? "rotate-180" : ""}`}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>

            {/* Dropdown panel */}
            <div
              className={`absolute top-[calc(100%+12px)] right-0 w-[252px] rounded-[12px] border border-[rgba(109,211,167,.12)] bg-[rgba(4,14,24,.97)] backdrop-blur-[20px] shadow-[0_16px_48px_rgba(0,0,0,.55)] transition-all duration-200 z-[600] overflow-hidden ${
                programsOpen
                  ? "opacity-100 pointer-events-auto translate-y-0"
                  : "opacity-0 pointer-events-none -translate-y-[6px]"
              }`}
            >
              {/* AI track */}
              <Link
                to="/ai-track"
                onClick={() => setProgramsOpen(false)}
                className="flex items-center gap-3 px-[14px] py-[13px] no-underline transition-colors duration-150 hover:bg-[rgba(216,90,48,.06)] group"
              >
                <span className="w-[34px] h-[34px] rounded-[8px] flex items-center justify-center flex-shrink-0 text-[1.1rem] bg-[rgba(216,90,48,.1)]">
                  🤖
                </span>
                <div>
                  <p className="font-cairo font-bold text-[.84rem] text-t-primary group-hover:text-[#D85A30] transition-colors duration-150 leading-snug">
                    مسار الذكاء الاصطناعي
                  </p>
                  <p className="font-mono text-[.46rem] text-t-dim tracking-[.08em] uppercase mt-[1px]">
                    AI IN BUSINESS · ٥ محطات
                  </p>
                </div>
              </Link>

              <div className="mx-[14px] h-px bg-[rgba(109,211,167,.07)]" />

              {/* Cyber track */}
              <Link
                to="/cyber-track"
                onClick={() => setProgramsOpen(false)}
                className="flex items-center gap-3 px-[14px] py-[13px] no-underline transition-colors duration-150 hover:bg-[rgba(26,144,112,.06)] group"
              >
                <span className="w-[34px] h-[34px] rounded-[8px] flex items-center justify-center flex-shrink-0 text-[1.1rem] bg-[rgba(26,144,112,.1)]">
                  🔐
                </span>
                <div>
                  <p className="font-cairo font-bold text-[.84rem] text-t-primary group-hover:text-[#22B58A] transition-colors duration-150 leading-snug">
                    مسار الأمن السيبراني
                  </p>
                  <p className="font-mono text-[.46rem] text-t-dim tracking-[.08em] uppercase mt-[1px]">
                    CYBERSECURITY · ٥ محطات
                  </p>
                </div>
              </Link>

              <div className="mx-[14px] h-px bg-[rgba(109,211,167,.07)]" />

              {/* All programs anchor */}
              <a
                href="#programs"
                onClick={() => setProgramsOpen(false)}
                className="flex items-center justify-between px-[14px] py-[10px] no-underline transition-colors duration-150 hover:bg-[rgba(109,211,167,.04)]"
              >
                <span className="font-cairo text-[.76rem] text-t-dim hover:text-t-sec transition-colors duration-150">
                  كل البرامج
                </span>
                <svg
                  className="w-[9px] h-[9px] text-t-dim"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </a>
            </div>
          </li>

          {/* rest of links */}
          {otherLinks.map(({ href, label }) => (
            <li key={href}>
              <a
                href={href}
                className={`nav-link ${activeLink === href.replace("#", "") ? "active" : ""}`}
              >
                {label}
              </a>
            </li>
          ))}
        </ul>

        {/* Desktop CTA */}
        <a
          href="https://store.afaaqwetaan.com"
          className="hidden md:block font-cairo font-bold text-[.88rem] bg-gold text-ink px-5 py-2 rounded-[4px] no-underline whitespace-nowrap flex-shrink-0 transition-all duration-200 hover:opacity-90 hover:-translate-y-px"
          target="_blank"
          rel="noopener"
        >
          سجّل الآن
        </a>

        {/* Hamburger */}
        <button
          className="md:hidden flex flex-col justify-center gap-[5px] w-9 h-9 bg-transparent border-0 cursor-pointer p-1.5 rounded flex-shrink-0 hover:bg-[rgba(109,211,167,.08)] transition-colors"
          onClick={toggleMenu}
          aria-label={isOpen ? "إغلاق القائمة" : "فتح القائمة"}
          aria-expanded={isOpen}
        >
          <span className="nb-line" />
          <span className="nb-line" />
          <span className="nb-line" />
        </button>
      </nav>

      {/* ── Mobile menu ── */}
      <div
        className={`nav-mobile ${isOpen ? "open" : ""}`}
        role="dialog"
        aria-label="القائمة الرئيسية"
        aria-hidden={!isOpen}
      >
        <ul
          className="list-none border-b border-[rgba(109,211,167,0.13)]"
          role="list"
        >
          {/* AI track */}
          <li className="border-b border-[rgba(109,211,167,.06)]">
            <Link
              to="/ai-track"
              onClick={closeMenu}
              className="flex items-center justify-between px-[clamp(16px,5vw,60px)] py-4 no-underline transition-all duration-200 hover:bg-[rgba(216,90,48,.05)] group"
            >
              <span className="flex items-center gap-3">
                <span className="w-[30px] h-[30px] rounded-[7px] flex items-center justify-center text-[.9rem] bg-[rgba(216,90,48,.1)] flex-shrink-0">
                  🤖
                </span>
                <span className="font-cairo font-medium text-[.95rem] text-t-sec group-hover:text-[#D85A30] transition-colors duration-150">
                  مسار الذكاء الاصطناعي
                </span>
              </span>
              <span className="font-mono text-[.6rem] text-t-dim">←</span>
            </Link>
          </li>

          {/* Cyber track */}
          <li className="border-b border-[rgba(109,211,167,.06)]">
            <Link
              to="/cyber-track"
              onClick={closeMenu}
              className="flex items-center justify-between px-[clamp(16px,5vw,60px)] py-4 no-underline transition-all duration-200 hover:bg-[rgba(26,144,112,.05)] group"
            >
              <span className="flex items-center gap-3">
                <span className="w-[30px] h-[30px] rounded-[7px] flex items-center justify-center text-[.9rem] bg-[rgba(26,144,112,.1)] flex-shrink-0">
                  🔐
                </span>
                <span className="font-cairo font-medium text-[.95rem] text-t-sec group-hover:text-[#22B58A] transition-colors duration-150">
                  مسار الأمن السيبراني
                </span>
              </span>
              <span className="font-mono text-[.6rem] text-t-dim">←</span>
            </Link>
          </li>

          {/* rest of mobile links */}
          {mobileLinks.map(({ href, label }, i) => (
            <li
              key={href}
              className={
                i < mobileLinks.length - 1
                  ? "border-b border-[rgba(109,211,167,.06)]"
                  : ""
              }
            >
              <a
                href={href}
                className="flex items-center justify-between px-[clamp(16px,5vw,60px)] py-4 text-t-sec text-[.95rem] font-medium no-underline transition-all duration-200 hover:text-t-primary hover:bg-[rgba(109,211,167,.04)]"
                onClick={closeMenu}
              >
                {label}
                <span className="text-[.6rem] text-t-dim font-mono">←</span>
              </a>
            </li>
          ))}
        </ul>

        <div className="flex flex-col gap-2.5 px-[clamp(16px,5vw,60px)] py-4 pb-5">
          <a
            href="https://store.afaaqwetaan.com"
            className="font-cairo font-bold text-[.95rem] bg-gold text-ink px-6 py-3 rounded-[4px] no-underline text-center hover:opacity-90 transition-opacity"
            target="_blank"
            rel="noopener"
            onClick={closeMenu}
          >
            سجّل الآن — المتجر
          </a>
          <a
            href="https://linktr.ee/AfaaqWetaan"
            className="font-cairo font-medium text-[.88rem] border border-[rgba(247,242,233,.15)] text-t-sec px-6 py-3 rounded-[4px] no-underline text-center transition-all hover:border-[rgba(247,242,233,.35)] hover:text-t-primary"
            target="_blank"
            rel="noopener"
            onClick={closeMenu}
          >
            انضم للمجتمع مجاناً
          </a>
        </div>

        <div className="flex items-center gap-1.5 border-t border-[rgba(109,211,167,.06)] px-[clamp(16px,5vw,60px)] py-2.5 pb-3.5">
          <span className="text-[.38rem] text-mint">◆</span>
          <span className="font-mono text-[.5rem] tracking-[.14em] uppercase text-t-dim">
            AFAAQ · آفاق توصلك للآفاق · وتان ٢٠٢٦
          </span>
        </div>
      </div>
    </>
  );
}
